// ---------------------------------------------------------------------------
// card-grid-controller — single source of truth for card order/visibility/size
// ---------------------------------------------------------------------------
//
// Generic, app-agnostic. Owns `order` + `hidden` + `sizes`. ALL mutations (drag,
// keyboard move, hide, show, resize) funnel through `applyMutation`: pure reducer
// → no-op guard → commit → single rerender → debounced persist. Rerender derives
// the visible list from source and reconciles the host grid DOM keyed by stable
// card id. Persist is debounced, no-op-guarded, freshness-checked after await,
// and never lets a rejection escape (degrade: log + non-blocking toast, keep
// in-memory state). Cards are rendered via the injected `renderItem` — the core
// never reads app concerns (auth/navigate).

import { createElement } from '../../core/dom/helpers.js';
import { attachReorderable, REORDER_KEY_ATTR } from '../../core/dnd/reorderable-list.js';
import { attachResizable } from '../../core/dnd/attach-resizable.js';
import { isAbortError } from '../../core/http/is-abort-error.js';
import { createCardGridChrome } from './card-grid-chrome.js';
import {
  normalizeLayout, moveCard, moveUp, moveDown, hideCard, showCard, setCardSize,
  effectiveSize, deriveVisible, deriveHidden, layoutEquals,
} from './card-grid-reducer.js';

/** Debounce window (ms) before persisting a layout change. */
const PERSIST_DEBOUNCE_MS = 600;

/** Absolute span ceilings. Per-card `maxColSpan`/`maxRowSpan` must fall within these. */
const COL_MAX_ABS = 4;
const ROW_MAX_ABS = 6;

/**
 * Validate a descriptor set and resolve each card's per-card resize maxima.
 * Throws (fail fast) on: a duplicate id; a `maxColSpan`/`maxRowSpan` that isn't a
 * finite integer within bounds (cols 1..4, rows 1..6); or a default span that
 * exceeds the card's own max. A bad max would otherwise let the reducer clamp
 * persisted sizes outside the contract while the chrome clamps differently.
 *
 * Exported so a consumer (e.g. createCrudPage) can run the SAME check
 * synchronously before an async mount — config errors fail fast, not as a
 * rejected promise. `createCardGrid` calls it too (defense in depth).
 *
 * @param {CardDescriptor[]} descriptors
 * @returns {Map<string, {colMax: number, rowMax: number}>} per-card maxima by id
 */
export function resolveCardBounds(descriptors) {
  /** @type {Map<string, {colMax: number, rowMax: number}>} */
  const maxById = new Map();
  /** @param {string} id @param {string} axis @param {unknown} v @param {number} fallback @param {number} abs */
  const resolveMax = (id, axis, v, fallback, abs) => {
    if (v === undefined) return fallback;
    if (typeof v !== 'number' || !Number.isInteger(v) || v < 1 || v > abs) {
      throw new Error(`card-grid: card '${id}' ${axis} must be an integer 1..${abs}, got ${v}`);
    }
    return v;
  };
  for (const d of descriptors) {
    if (maxById.has(d.id)) throw new Error(`card-grid: duplicate card descriptor id '${d.id}'`);
    const colMax = resolveMax(d.id, 'maxColSpan', d.capabilities?.resize?.maxColSpan, 4, COL_MAX_ABS);
    const rowMax = resolveMax(d.id, 'maxRowSpan', d.capabilities?.resize?.maxRowSpan, 2, ROW_MAX_ABS);
    if ((d.colSpan ?? 1) > colMax || (d.rowSpan ?? 1) > rowMax) {
      throw new Error(`card-grid: card '${d.id}' default size exceeds its resize max (colMax ${colMax}, rowMax ${rowMax})`);
    }
    maxById.set(d.id, { colMax, rowMax });
  }
  return maxById;
}

/** Default labels (reproduce the original dashboard strings). @type {Required<CardGridLabels>} */
const DEFAULT_LABELS = {
  hiddenLabel: 'Hidden:',
  saveErrorToast: 'Couldn’t save layout',
  dragToReorder: 'Drag to reorder',
  moveUp: (t) => `Move ${t} up`,
  moveDown: (t) => `Move ${t} down`,
  widthDown: (t) => `Decrease width of ${t}`,
  widthUp: (t) => `Increase width of ${t}`,
  heightDown: (t) => `Decrease height of ${t}`,
  heightUp: (t) => `Increase height of ${t}`,
  hide: (t) => `Hide ${t}`,
};

/**
 * @typedef {import('../../contracts/card-grid.js').CardDescriptor} CardDescriptor
 * @typedef {import('../../contracts/card-grid.js').CardHandle} CardHandle
 * @typedef {import('../../contracts/card-grid.js').CardGridLayout} CardGridLayout
 * @typedef {import('../../contracts/card-grid.js').CardGridLayoutStore} CardGridLayoutStore
 */

/**
 * @typedef {Object} CardGridLabels
 * @property {string} [hiddenLabel]
 * @property {string} [saveErrorToast]
 * @property {string} [dragToReorder]
 * @property {(title: string) => string} [moveUp]
 * @property {(title: string) => string} [moveDown]
 * @property {(title: string) => string} [widthDown]
 * @property {(title: string) => string} [widthUp]
 * @property {(title: string) => string} [heightDown]
 * @property {(title: string) => string} [heightUp]
 * @property {(title: string) => string} [hide]
 */

/**
 * @param {{
 *   items: CardDescriptor[],
 *   initialLayout: { order?: string[], hidden?: string[], sizes?: Record<string, {colSpan?: number, rowSpan?: number}> },
 *   layoutStore: CardGridLayoutStore,
 *   renderItem: (item: CardDescriptor) => CardHandle,
 *   gridContainer: HTMLElement,
 *   hiddenContainer: HTMLElement,
 *   labels?: CardGridLabels,
 *   logger: { info: Function, error: Function },
 *   eventBus: { emit: (name: string, data?: unknown) => void },
 *   signal: AbortSignal,
 *   measureGrid?: (grid: HTMLElement) => { colCellPx: number, rowCellPx: number, colGapPx: number, rowGapPx: number },
 * }} deps
 * @returns {{ destroy: () => void, refresh: () => void }}
 */
export function createCardGrid(deps) {
  const grid = deps.gridContainer;
  const hiddenMenu = deps.hiddenContainer;
  const descriptors = deps.items;
  const renderItem = deps.renderItem;
  const store = deps.layoutStore;
  const { logger, eventBus, signal } = deps;
  const labels = { ...DEFAULT_LABELS, ...(deps.labels || {}) };

  const knownIds = descriptors.map((d) => d.id);
  // Validate ids + per-card maxima + default≤max (dup id, bad/oversized max, etc.).
  // Single-sourced so a consumer can run the SAME check synchronously pre-mount.
  const maxById = resolveCardBounds(descriptors);
  /** @type {Map<string, CardDescriptor>} */
  const byId = new Map(descriptors.map((d) => [d.id, d]));
  /** Descriptor default sizes — shared by normalize + setCardSize (minimal storage). */
  /** @type {Map<string, {colSpan?: number, rowSpan?: number}>} */
  const defaultsById = new Map(descriptors.map((d) => [d.id, { colSpan: d.colSpan, rowSpan: d.rowSpan }]));
  /** Ids whose `capabilities.hideable === false` — never hidden (normalize forces visible). */
  const nonHideableIds = new Set(descriptors.filter((d) => d.capabilities?.hideable === false).map((d) => d.id));

  /** @type {CardGridLayout} */
  let layout = normalizeLayout(deps.initialLayout || {}, knownIds, defaultsById, nonHideableIds, maxById);

  let destroyed = false;
  // Persistence freshness/flush tracking: dirtyVersion bumps on every mutation;
  // persistedVersion advances ONLY when a save actually COMPLETES (never merely
  // when launched — a save aborted by the page signal must not look clean).
  // Pending ≡ dirtyVersion > persistedVersion.
  let dirtyVersion = 0;
  let persistedVersion = 0;
  /** @type {ReturnType<typeof setTimeout> | null} */
  let persistTimer = null;

  /** Mounted card handles + chrome, keyed by card id (for reconciliation). */
  /** @type {Map<string, { chrome: ReturnType<typeof createCardGridChrome>, handle: CardHandle }>} */
  const mounted = new Map();

  // -------------------------------------------------------------------------
  // Per-breakpoint resize bounds. colMax derives from the live grid track count
  // (guarded for jsdom → falls back to 4); rowMax is 2 non-mobile, 1 on mobile.
  // -------------------------------------------------------------------------
  const mqMobile = typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(max-width: 767px)') : null;
  const mqDesktop = typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(min-width: 1024px)') : null;

  const isMobile = () => !!mqMobile?.matches;

  /** Breakpoint envelope (max across ALL cards). @returns {{ colMax: number, rowMax: number }} */
  function currentBounds() {
    if (isMobile()) return { colMax: 1, rowMax: 1 };
    let colMax = 4;
    if (typeof getComputedStyle === 'function') {
      const cols = (getComputedStyle(grid).gridTemplateColumns || '').split(' ').filter(Boolean).length;
      if (cols > 0) colMax = cols;
    } else if (mqDesktop && !mqDesktop.matches) {
      colMax = 2; // tablet
    }
    return { colMax, rowMax: ROW_MAX_ABS };
  }

  /**
   * Effective resize bounds for ONE card = breakpoint envelope ∩ the card's own
   * per-card max. So a stat card never grows past 2 rows and the table never past
   * its `maxRowSpan`, while the breakpoint still caps columns on tablet/mobile.
   * @param {string} id @param {{colMax: number, rowMax: number}} bp
   * @returns {{ colMax: number, rowMax: number }}
   */
  function boundsFor(id, bp) {
    const m = maxById.get(id) || { colMax: 4, rowMax: 2 };
    return { colMax: Math.min(bp.colMax, m.colMax), rowMax: Math.min(bp.rowMax, m.rowMax) };
  }

  // -------------------------------------------------------------------------
  // Mutation pipeline — the ONLY place layout state changes.
  // -------------------------------------------------------------------------
  /** @param {(l: CardGridLayout) => CardGridLayout} mutator */
  function applyMutation(mutator) {
    const next = mutator(layout);
    if (layoutEquals(next, layout)) return; // no-op: no rerender, no persist
    layout = next;
    dirtyVersion += 1;
    rerender();
    schedulePersist();
  }

  function schedulePersist() {
    if (persistTimer) clearTimeout(persistTimer);
    persistTimer = setTimeout(() => {
      persistTimer = null;
      persistNow(signal).catch(handleSaveError);
    }, PERSIST_DEBOUNCE_MS);
  }

  /**
   * Launch a save for the CURRENT dirtyVersion. persistedVersion advances only
   * on success — so an aborted/failed save leaves the work still pending.
   * @param {AbortSignal | undefined} saveSignal
   * @returns {Promise<void>}
   */
  function persistNow(saveSignal) {
    const version = dirtyVersion;
    const snapshot = { order: [...layout.order], hidden: [...layout.hidden], sizes: { ...layout.sizes } };
    logger.info('Card grid layout save start');
    return store.save(snapshot, saveSignal).then(() => {
      persistedVersion = Math.max(persistedVersion, version);
      if (!destroyed) logger.info('Card grid layout save success');
    });
  }

  /**
   * Flush a pending change immediately, bypassing the debounce, with NO page
   * signal so the write survives teardown (app-shell aborts the page signal
   * before calling destroy()). No-op when nothing is pending.
   */
  function flushPersist() {
    if (persistTimer) { clearTimeout(persistTimer); persistTimer = null; }
    if (dirtyVersion <= persistedVersion) return;
    persistNow(undefined).catch(handleSaveError);
  }

  /** @param {unknown} err */
  function handleSaveError(err) {
    if (isAbortError(err)) return; // aborted save: still pending, not a failure
    logger.error('Card grid layout save failed', { data: err });
    if (!destroyed) {
      eventBus.emit('toast.message.requested', { message: labels.saveErrorToast, type: 'warning' });
    }
    // Keep in-memory state (optimistic); no rollback mid-session.
  }

  // -------------------------------------------------------------------------
  // Render — derive visible list from source, reconcile DOM keyed by id.
  // -------------------------------------------------------------------------
  function rerender() {
    const visible = deriveVisible(layout.order, layout.hidden, byId);
    const visibleIds = visible.map((d) => d.id);

    // Destroy + remove cards no longer visible.
    for (const [id, entry] of mounted) {
      if (!visibleIds.includes(id)) {
        entry.handle.destroy();
        entry.chrome.destroy();
        entry.chrome.el.remove();
        mounted.delete(id);
      }
    }

    const bounds = currentBounds();

    // Ensure each visible card exists, with current move-boundary state, in order.
    visible.forEach((descriptor, index) => {
      let entry = mounted.get(descriptor.id);
      if (!entry) {
        entry = mountCard(descriptor, index, visible.length, bounds);
        mounted.set(descriptor.id, entry);
      } else {
        updateBoundaries(entry.chrome.el, index, visible.length);
        // Apply the effective size + PER-CARD bounds in place (no remount → no flicker).
        entry.chrome.setSpan(effectiveSize(layout.sizes, descriptor.id, descriptor));
        const cb = boundsFor(descriptor.id, bounds);
        entry.chrome.setResizeBounds(cb.colMax, cb.rowMax);
      }
      // Place in order, but only touch the DOM when the node is NOT already in
      // the right slot. Re-inserting an already-correct node (e.g. on a pure
      // resize, where order is unchanged) defeats the browser's scroll-anchor
      // and yanks the viewport to the top / churns focus. Position-aware insert
      // keeps a size-only change a true no-move.
      const desired = grid.children[index];
      if (desired !== entry.chrome.el) {
        grid.insertBefore(entry.chrome.el, desired ?? null);
      }
    });

    renderHiddenMenu();
  }

  /**
   * @param {CardDescriptor} descriptor @param {number} index @param {number} total
   * @param {{colMax: number, rowMax: number}} bounds - breakpoint envelope (intersected per-card here).
   */
  function mountCard(descriptor, index, total, bounds) {
    const eff = effectiveSize(layout.sizes, descriptor.id, descriptor);
    const cb = boundsFor(descriptor.id, bounds);
    const chrome = createCardGridChrome({
      id: descriptor.id,
      title: descriptor.title,
      kind: descriptor.kind,
      capabilities: descriptor.capabilities,
      labels,
      onHide: () => applyMutation((l) => hideCard(l, descriptor.id)),
      onMoveUp: () => applyMutation((l) => ({ ...l, order: moveUp(l.order, l.hidden, descriptor.id) })),
      onMoveDown: () => applyMutation((l) => ({ ...l, order: moveDown(l.order, l.hidden, descriptor.id) })),
      canMoveUp: index > 0,
      canMoveDown: index < total - 1,
      colSpan: /** @type {1|2|3|4} */ (eff.colSpan),
      rowSpan: /** @type {import('../../contracts/card-grid.js').CardGridRowSpan} */ (eff.rowSpan),
      colMax: cb.colMax,
      rowMax: cb.rowMax,
      onResize: (axis, delta) => resizeByKeyboard(descriptor.id, axis, delta),
    });
    const handle = renderItem(descriptor);
    chrome.body.appendChild(handle.el);
    return { chrome, handle };
  }

  /**
   * Keyboard resize: step the RAW span by ±1 on an axis (clamped 1..absMax by the
   * reducer). "+" is already disabled at the breakpoint max in card-chrome; "−"
   * steps an over-max card down. Funnels through applyMutation like every mutation.
   * @param {string} id @param {'col'|'row'} axis @param {-1|1} delta
   */
  function resizeByKeyboard(id, axis, delta) {
    const descriptor = byId.get(id);
    if (!descriptor) return;
    const eff = effectiveSize(layout.sizes, id, descriptor);
    const next = axis === 'col'
      ? { colSpan: eff.colSpan + delta, rowSpan: eff.rowSpan }
      : { colSpan: eff.colSpan, rowSpan: eff.rowSpan + delta };
    applyMutation((l) => setCardSize(l, id, next, defaultsById, boundsFor(id, currentBounds())));
  }

  /**
   * Update the move-up/down disabled state on an already-mounted card.
   * @param {HTMLElement} cardEl @param {number} index @param {number} total
   */
  function updateBoundaries(cardEl, index, total) {
    const up = /** @type {HTMLButtonElement|null} */ (cardEl.querySelector('.card-grid__card-btn--move-up'));
    const down = /** @type {HTMLButtonElement|null} */ (cardEl.querySelector('.card-grid__card-btn--move-down'));
    if (up) up.disabled = index === 0;
    if (down) down.disabled = index === total - 1;
  }

  function renderHiddenMenu() {
    const hidden = deriveHidden(layout.hidden, byId);
    if (!hidden.length) {
      hiddenMenu.replaceChildren();
      hiddenMenu.hidden = true;
      return;
    }
    hiddenMenu.hidden = false;
    const label = createElement('span', { class: 'card-grid__hidden-label' }, [labels.hiddenLabel]);
    const buttons = hidden.map((d) => {
      const btn = /** @type {HTMLButtonElement} */ (createElement('button', {
        type: 'button',
        class: 'card-grid__hidden-item',
      }, [d.title]));
      btn.addEventListener('click', () => applyMutation((l) => showCard(l, d.id)));
      return btn;
    });
    hiddenMenu.replaceChildren(label, ...buttons);
  }

  // -------------------------------------------------------------------------
  // Drag reorder — reports moves by key; the controller mutates.
  // -------------------------------------------------------------------------
  const reorderable = attachReorderable(grid, {
    itemSelector: '.card-grid__card',
    handleSelector: '.card-grid__card-handle',
    getKeys: () => deriveVisible(layout.order, layout.hidden, byId).map((d) => d.id),
    onMove: (fromKey, toKey) => applyMutation((l) => ({ ...l, order: moveCard(l.order, fromKey, toKey) })),
  });

  // -------------------------------------------------------------------------
  // Drag resize — edge handles report a snapped span; the controller mutates.
  // -------------------------------------------------------------------------
  const resizable = attachResizable(grid, {
    itemSelector: '.card-grid__card',
    keyAttr: REORDER_KEY_ATTR,
    colHandleSelector: '.card-grid__card-resize--col',
    rowHandleSelector: '.card-grid__card-resize--row',
    isBlocked: () => isMobile(),
    getSpans: (id) => {
      const descriptor = byId.get(id);
      const eff = effectiveSize(layout.sizes, id, /** @type {any} */ (descriptor) || {});
      const cb = boundsFor(id, currentBounds());
      return { colSpan: eff.colSpan, rowSpan: eff.rowSpan, colMax: cb.colMax, rowMax: cb.rowMax };
    },
    onResize: (id, size) => applyMutation((l) => setCardSize(l, id, size, defaultsById, boundsFor(id, currentBounds()))),
    ...(deps.measureGrid ? { measureGrid: deps.measureGrid } : {}),
  });

  // Re-derive bounds + refresh size-button state on breakpoint changes.
  const onBreakpointChange = () => { if (!destroyed) rerender(); };
  mqMobile?.addEventListener?.('change', onBreakpointChange);
  mqDesktop?.addEventListener?.('change', onBreakpointChange);

  // Initial paint.
  rerender();

  return {
    /** Re-run each mounted card's data load in place (post-mutation freshness). */
    refresh() {
      for (const entry of mounted.values()) entry.handle.refresh?.();
    },
    destroy() {
      // Flush a pending layout change BEFORE marking destroyed — the page signal
      // is already aborted by app-shell, so flushPersist() saves with no signal.
      flushPersist();
      destroyed = true;
      reorderable.destroy();
      resizable.destroy();
      mqMobile?.removeEventListener?.('change', onBreakpointChange);
      mqDesktop?.removeEventListener?.('change', onBreakpointChange);
      for (const entry of mounted.values()) {
        entry.handle.destroy();
        entry.chrome.destroy();
        entry.chrome.el.remove();
      }
      mounted.clear();
      hiddenMenu.replaceChildren();
    },
  };
}
